import { useMemo } from 'react';
import {
  ReceiptRequireDeliveryInfo,
  ReceiptRequireReceiptDate,
} from 'src/constants/permissionsList';
import usePermission from 'src/hooks/usePermissions';
import { IReceipt } from 'src/types/Receipt';
import { validateDateFormat } from 'src/utils/formatTime';

const useReceiptDetailsInfoErrors = (receipt: IReceipt) => {
  const isReceiptRequireDeliveryInfo = usePermission({ permissions: ReceiptRequireDeliveryInfo });
  const isReceiptRequireReceiptDate = usePermission({ permissions: ReceiptRequireReceiptDate });

  const documentDateErrors = useMemo(() => {
    if (isReceiptRequireReceiptDate && !receipt?.documentDate) {
      return ['Document Date is required'];
    } else if (!validateDateFormat(receipt._documentDate)) {
      return ['Document Date is invalid'];
    }
    return [];
  }, [isReceiptRequireReceiptDate, receipt._documentDate, receipt?.documentDate]);

  const deliveryDateTimeErrors = useMemo(() => {
    if (isReceiptRequireDeliveryInfo && !receipt?.deliveryDateTime) {
      return ['Delivery Date is required'];
    } else if (!validateDateFormat(receipt._deliveryDateTime)) {
      return ['Delivery Date is invalid'];
    }
    return [];
  }, [isReceiptRequireDeliveryInfo, receipt._deliveryDateTime, receipt?.deliveryDateTime]);

  const deliveryDriverNameErrors = useMemo(
    () =>
      !receipt?.deliveryDriverName && isReceiptRequireDeliveryInfo
        ? ['Delivery Driver Name is required']
        : receipt?.deliveryDriverName?.trim()?.length > 255
        ? ['Delivery Driver Name is more than 255 characters']
        : [],
    [isReceiptRequireDeliveryInfo, receipt?.deliveryDriverName]
  );
  const deliveryCompanyNameErrors = useMemo(
    () =>
      !receipt?.deliveryCompanyName && isReceiptRequireDeliveryInfo
        ? ['Delivery Company Name is required']
        : receipt?.deliveryCompanyName?.trim().length > 255
        ? ['Delivery Company Name is more than 255 characters']
        : [],
    [isReceiptRequireDeliveryInfo, receipt?.deliveryCompanyName]
  );
  const noteErrors = useMemo(
    () =>
      String(receipt?.receiptNotes).trim().length > 255
        ? ['Notes are more than 255 characters']
        : [],
    [receipt?.receiptNotes]
  );
  return {
    documentDateErrors,
    deliveryDateTimeErrors,
    deliveryDriverNameErrors,
    deliveryCompanyNameErrors,
    noteErrors,
  };
};

export default useReceiptDetailsInfoErrors;
